import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { temporal } from 'zundo';
import { AlbumInfo, Track } from '../types/album';
import { CornerStyle, DiscDisplayMode, PrintUnitType, TextAlignment, TextDirection, ColumnLayoutType } from '../types/enums';
import {
  BackgroundSettings,
  CornerSettings,
  PRINT_UNIT_DIMENSIONS,
  PrintUnit,
  TextSettings,
  ImageFilter,
  BackTracklistUnit,
  DiscUnit2
} from '../types/printUnit';
import { Layer } from '../types/layer';
import { generateUUID } from '../lib/utils';
import {
  syncAlbumContentOnInfoChange,
  syncAlbumContentOnSelect,
} from '../utils/albumSync';
import { useOutpaintingStore } from './outpaintingStore';

// 기본 블러 강도 설정
const DEFAULT_BLUR_INTENSITY = 0;

// 기본 이미지 필터 설정
const DEFAULT_IMAGE_FILTER = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  blur: 0
};

// 기본 모서리 설정
const DEFAULT_CORNER_SETTINGS: CornerSettings = {
  topLeft: { style: CornerStyle.NONE, size: 0 },
  topRight: { style: CornerStyle.NONE, size: 0 },
  bottomLeft: { style: CornerStyle.NONE, size: 0 },
  bottomRight: { style: CornerStyle.NONE, size: 0 }
};

// 기본 배경 설정
const DEFAULT_BACKGROUND_SETTINGS: BackgroundSettings = {
  image: null,
  scale: 1,
  x: 0,
  y: 0,
  color: '#ffffff',
  transparent: false,
};

// 기본 텍스트 설정
const DEFAULT_TEXT_SETTINGS: TextSettings = {
  color: '#000000',
  size: 40,
  autoSize: true,
  alignment: TextAlignment.CENTER,
  direction: TextDirection.HORIZONTAL,
  fontFamily: 'Pretendard',
  paddingLeft: 0,
  paddingRight: 0
};

// 초기 인쇄부 설정
const DEFAULT_PRINT_UNITS: Record<PrintUnitType, PrintUnit> = {
  // 전면 커버 (인쇄부 1)
  [PrintUnitType.FRONT]: {
    type: PrintUnitType.FRONT,
    name: '인쇄부 1 (전면 커버)',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.FRONT],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: true,
    useBackground: true,
    useText: true, // 텍스트 사용 기본값 true로 변경
    text: { ...DEFAULT_TEXT_SETTINGS }, // 텍스트 설정 추가
    showTitle: true, // 앨범명 표시 기본값
    showArtist: true, // 가수명 표시 기본값
    titlePosition: { x: 60, y: 60 }, // 앨범명 좌상단 ≈ 5mm 여백
    artistPosition: { x: 60, y: 140 }, // 가수명: 앨범명(titleFontSize 100) 아래 약 20px 간격
    titleFontSize: 100, // 앨범명 기본 글자 크기
    artistFontSize: 70, // 가수명 기본 글자 크기
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  },
  
  // 측면 (인쇄부 2)
  [PrintUnitType.SIDE]: {
    type: PrintUnitType.SIDE,
    name: '인쇄부 2 (측면)',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.SIDE],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: false,
    text: { ...DEFAULT_TEXT_SETTINGS, size: 70, autoSize: false },
    content: '',
    useBackground: true,
    useText: true,
    blurIntensity: DEFAULT_BLUR_INTENSITY,
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  },

  // 뒷면 상단 (인쇄부 3)
  [PrintUnitType.BACK_TOP]: {
    type: PrintUnitType.BACK_TOP,
    name: '인쇄부 3 (뒷면 상단)',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.BACK_TOP],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: false,
    text: { ...DEFAULT_TEXT_SETTINGS, size: 80, autoSize: false },
    content: '',
    useTwoLines: false,
    useBackground: true,
    useText: true,
    blurIntensity: DEFAULT_BLUR_INTENSITY,
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  },
  
  // 뒷면 (인쇄부 4)
  [PrintUnitType.BACK]: {
    type: PrintUnitType.BACK,
    name: '인쇄부 4 (뒷면 곡 리스트)',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.BACK],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: true,
    text: { ...DEFAULT_TEXT_SETTINGS, size: 68, autoSize: false },
    useBackground: true,
    useText: true,
    columnLayout: ColumnLayoutType.AUTO,
    useTwoColumns: true,
    lineHeight: 1.2,
    blurIntensity: DEFAULT_BLUR_INTENSITY,
    // 흰색 투명도 레이어 설정
    showWhiteOverlay: false, // 기본값: 비활성화
    whiteOverlayOpacity: 0.5, // 기본값: 50% 투명도
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  },
  
  // 디스크 본체 2 (인쇄부 5)
  [PrintUnitType.DISC2]: {
    type: PrintUnitType.DISC2,
    name: '인쇄부 5 (본체(앨범자켓만 표시))',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.DISC2],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: true,
    text: { ...DEFAULT_TEXT_SETTINGS },
    displayMode: DiscDisplayMode.ALBUM_COVER_ONLY,
    useBackground: true,
    useText: true,
    showTitle: true,
    showArtist: true,
    titlePosition: { x: 30, y: 30 }, // 디스크 좌상단 ≈ 2.5mm 여백
    artistPosition: { x: 30, y: 75 }, // 가수명: 앨범명(titleFontSize 46) 아래 약 13px 간격
    titleFontSize: 46, // 앨범명 기본 글자 크기
    artistFontSize: 36, // 가수명 기본 글자 크기
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  },
  
  // 디스크 측면 (인쇄부 6)
  [PrintUnitType.DISC_SIDE]: {
    type: PrintUnitType.DISC_SIDE,
    name: '인쇄부 6 (디스크 측면)',
    dimension: PRINT_UNIT_DIMENSIONS[PrintUnitType.DISC_SIDE],
    background: { ...DEFAULT_BACKGROUND_SETTINGS },
    cornerSettings: { ...DEFAULT_CORNER_SETTINGS },
    showMdLogo: false,
    text: { ...DEFAULT_TEXT_SETTINGS },
    content: '',
    useBackground: true,
    useText: true,
    blurIntensity: DEFAULT_BLUR_INTENSITY,
    imageFilter: { ...DEFAULT_IMAGE_FILTER }
  }
};

// 스토어 상태 인터페이스
interface StoreState {
  // 앨범 정보
  albumInfo: AlbumInfo;
  setAlbumInfo: (info: Partial<AlbumInfo>) => void;

  // 곡 리스트
  tracklist: Track[];
  addTrack: (title: string) => void;
  updateTrack: (id: string, title: string) => void;
  removeTrack: (id: string) => void;
  setTracklist: (tracks: Track[]) => void;
  reorderTrack: (startIndex: number, endIndex: number) => void;

  // 인쇄부 관련
  printUnits: Record<PrintUnitType, PrintUnit>;
  selectedPrintUnit: PrintUnitType | null;
  setSelectedPrintUnit: (type: PrintUnitType) => void;
  updatePrintUnit: <T extends PrintUnitType>(type: T, updates: Partial<PrintUnit>) => void;

  // 인쇄부 옵션 패널 활성 탭 (이미지/배경색/모서리/로고/텍스트/필터)
  activePrintUnitOptionTab: string;
  setActivePrintUnitOptionTab: (tab: string) => void;

  // 클립보드 (스타일 복사/붙여넣기, persist 제외)
  clipboard: PrintUnit | null;
  setClipboard: (unit: PrintUnit) => void;
  applyClipboard: (targetType: PrintUnitType) => void;

  // 레이어 관련 (인쇄부별 레이어 목록, persist 포함)
  /** 인쇄부별 레이어 목록 — key: PrintUnitType */
  layers: Record<string, Layer[]>;
  /** 현재 선택된 레이어 ID */
  selectedLayerId: string | null;
  /** 레이어 선택 */
  selectLayer: (id: string | null) => void;
  /** 특정 인쇄부의 레이어 목록 반환 */
  getLayersForUnit: (unitType: PrintUnitType) => Layer[];
  /** 레이어 가시성 토글 */
  toggleLayerVisibility: (unitType: PrintUnitType, layerId: string) => void;
  /** 레이어 잠금 토글 */
  toggleLayerLock: (unitType: PrintUnitType, layerId: string) => void;
  /** 레이어 이름 변경 */
  renameLayer: (unitType: PrintUnitType, layerId: string, name: string) => void;
  /** 레이어 순서 변경 (oldIndex → newIndex) */
  reorderLayer: (unitType: PrintUnitType, oldIndex: number, newIndex: number) => void;
  /** 인쇄부의 레이어 목록을 printUnit 상태에서 동기화해 재생성 */
  syncLayersForUnit: (unitType: PrintUnitType) => void;

  // 레이아웃 관련
  resetAll: () => void;
  // 새 앨범 만들기용 초기화 함수 (페이지 리로드 없음)
  resetForNewAlbum: () => void;
  // 로컬 스토리지 리셋 함수
  resetStorage: () => void;
}

// 스타일 복사 시 유지할 safe 필드 목록 (content/텍스트 내용 제외)
const STYLE_SAFE_KEYS: (keyof PrintUnit)[] = ['background', 'cornerSettings', 'imageFilter'];

/**
 * printUnit 상태를 기반으로 해당 인쇄부의 레이어 목록을 생성한다.
 * 레이어 순서 (bottom → top): 배경 → 오버레이 → 로고 → 텍스트
 */
function buildLayersFromUnit(unitType: PrintUnitType, unit: PrintUnit, existing: Layer[]): Layer[] {
  // 기존 레이어가 있으면 visible/locked/name/zIndex 보존, 없으면 기본값 생성
  const findExisting = (id: string) => existing.find((l) => l.id === id);

  const makeLayer = (
    id: string,
    type: Layer['type'],
    name: string,
    defaultVisible: boolean,
  ): Layer => {
    const ex = findExisting(id);
    return {
      id,
      type,
      name: ex?.name ?? name,
      visible: ex?.visible ?? defaultVisible,
      locked: ex?.locked ?? false,
      zIndex: 0, // zIndex는 배열 순서로 결정, 필드는 참조용
    } as Layer;
  };

  const layers: Layer[] = [];

  // 배경 레이어 (항상 존재)
  layers.push(makeLayer(`${unitType}-background`, 'background', '배경', true));

  // 오버레이 레이어 (BACK 인쇄부의 흰색 투명도 레이어)
  const backUnit = unit as PrintUnit & { showWhiteOverlay?: boolean };
  if ('showWhiteOverlay' in unit) {
    layers.push(makeLayer(`${unitType}-overlay`, 'overlay', '흰색 오버레이', backUnit.showWhiteOverlay ?? false));
  }

  // 로고 레이어 (showMdLogo 지원 인쇄부)
  if ('showMdLogo' in unit) {
    layers.push(makeLayer(`${unitType}-logo`, 'logo', 'MD 로고', (unit as PrintUnit & { showMdLogo: boolean }).showMdLogo));
  }

  // 텍스트 레이어 (useText 지원 인쇄부)
  if ('useText' in unit) {
    layers.push(makeLayer(`${unitType}-text`, 'text', '텍스트', (unit as PrintUnit & { useText: boolean }).useText));
  }

  // zIndex를 배열 순서 기반으로 재할당
  return layers.map((l, i) => ({ ...l, zIndex: i }));
}

/** 모든 인쇄부의 초기 레이어 맵 생성 */
function buildInitialLayers(printUnits: Record<PrintUnitType, PrintUnit>): Record<string, Layer[]> {
  const result: Record<string, Layer[]> = {};
  for (const type of Object.values(PrintUnitType)) {
    result[type] = buildLayersFromUnit(type, printUnits[type], []);
  }
  return result;
}

// Zustand 스토어 생성
export const useStore = create<StoreState>()(
  temporal(
  persist(
    (set, get) => ({
      // 앨범 정보 초기값
      albumInfo: {
        title: '',
        artist: '',
        coverImage: null,
        tags: []
      },
      
      // 앨범 정보 수정 함수
      setAlbumInfo: (info) => {
        return set((state) => {
          const newAlbumInfo = { ...state.albumInfo, ...info };

          // 앨범 정보가 변경되면 SIDE/BACK_TOP/DISC_SIDE content 자동 업데이트
          // (제목/가수 중 하나라도 있을 때만 덮어쓰기, 둘 다 비면 기존 값 유지)
          const updatedPrintUnits = syncAlbumContentOnInfoChange(
            state.printUnits,
            newAlbumInfo,
          );

          return {
            albumInfo: newAlbumInfo,
            printUnits: updatedPrintUnits
          };
        });
      },
      
      // 곡 리스트 초기값
      tracklist: [],
      
      // 곡 추가 함수
      addTrack: (title) => 
        set((state) => ({ 
          tracklist: [...state.tracklist, { id: generateUUID(), title }] 
        })),
      
      // 곡 수정 함수
      updateTrack: (id, title) => 
        set((state) => ({ 
          tracklist: state.tracklist.map(track => 
            track.id === id ? { ...track, title } : track
          ) 
        })),
      
      // 곡 삭제 함수
      removeTrack: (id) => 
        set((state) => ({ 
          tracklist: state.tracklist.filter(track => track.id !== id) 
        })),
      
      // 곡 리스트 전체 설정 함수
      setTracklist: (tracks) => 
        set(() => ({ tracklist: tracks })),
      
      // 곡 순서 변경 함수
      reorderTrack: (startIndex, endIndex) => 
        set((state) => {
          const result = Array.from(state.tracklist);
          const [removed] = result.splice(startIndex, 1);
          result.splice(endIndex, 0, removed);
          
          return { tracklist: result };
        }),
      
      // 인쇄부 초기값
      printUnits: DEFAULT_PRINT_UNITS,
      
      // 선택된 인쇄부 초기값
      selectedPrintUnit: PrintUnitType.FRONT,
      
      // 인쇄부 선택 함수
      setSelectedPrintUnit: (type) =>
        set((state) => {
          // 선택된 인쇄부가 SIDE/BACK_TOP/DISC_SIDE이고 기존 content가 비어있을 때만
          // 앨범 정보로 자동 채우기 (기존 커스텀 content는 보존)
          const updatedPrintUnits = syncAlbumContentOnSelect(
            state.printUnits,
            type,
            state.albumInfo,
          );

          return {
            selectedPrintUnit: type,
            printUnits: updatedPrintUnits
          };
        }),
      
      // 인쇄부 업데이트 함수
      updatePrintUnit: (type, updates) => {
        set((state) => {
          const newPrintUnit = { ...state.printUnits[type], ...updates };
          return {
            printUnits: {
              ...state.printUnits,
              [type]: newPrintUnit
            }
          };
        });
      },

      // 인쇄부 옵션 패널 활성 탭 초기값
      activePrintUnitOptionTab: 'image',
      setActivePrintUnitOptionTab: (tab) => set({ activePrintUnitOptionTab: tab }),

      // 클립보드 초기값 (persist 제외)
      clipboard: null,

      // 현재 인쇄부의 스타일을 클립보드에 복사
      setClipboard: (unit: PrintUnit) => set({ clipboard: structuredClone(unit) }),

      // 클립보드의 스타일을 대상 인쇄부에 적용 (background, cornerSettings, imageFilter만)
      applyClipboard: (targetType: PrintUnitType) => {
        const cb = get().clipboard;
        if (!cb) return;
        const target = get().printUnits[targetType];
        if (!target) return;
        const merged: PrintUnit = { ...target };
        for (const key of STYLE_SAFE_KEYS) {
          if (key in cb) {
            (merged as Record<string, unknown>)[key] = structuredClone((cb as Record<string, unknown>)[key]);
          }
        }
        get().updatePrintUnit(targetType, merged);
      },

      // 레이어 초기값
      layers: buildInitialLayers(DEFAULT_PRINT_UNITS),
      selectedLayerId: null,

      // 레이어 선택
      selectLayer: (id) => set({ selectedLayerId: id }),

      // 특정 인쇄부의 레이어 목록 반환
      getLayersForUnit: (unitType) => {
        return get().layers[unitType] ?? [];
      },

      // 레이어 가시성 토글
      toggleLayerVisibility: (unitType, layerId) => {
        set((state) => {
          const unitLayers = state.layers[unitType] ?? [];
          return {
            layers: {
              ...state.layers,
              [unitType]: unitLayers.map((l) =>
                l.id === layerId ? { ...l, visible: !l.visible } : l
              ),
            },
          };
        });
      },

      // 레이어 잠금 토글
      toggleLayerLock: (unitType, layerId) => {
        set((state) => {
          const unitLayers = state.layers[unitType] ?? [];
          return {
            layers: {
              ...state.layers,
              [unitType]: unitLayers.map((l) =>
                l.id === layerId ? { ...l, locked: !l.locked } : l
              ),
            },
          };
        });
      },

      // 레이어 이름 변경
      renameLayer: (unitType, layerId, name) => {
        set((state) => {
          const unitLayers = state.layers[unitType] ?? [];
          return {
            layers: {
              ...state.layers,
              [unitType]: unitLayers.map((l) =>
                l.id === layerId ? { ...l, name } : l
              ),
            },
          };
        });
      },

      // 레이어 순서 변경 (oldIndex → newIndex)
      reorderLayer: (unitType, oldIndex, newIndex) => {
        set((state) => {
          const unitLayers = [...(state.layers[unitType] ?? [])];
          if (oldIndex < 0 || newIndex < 0 || oldIndex >= unitLayers.length || newIndex >= unitLayers.length) {
            return {};
          }
          const [moved] = unitLayers.splice(oldIndex, 1);
          unitLayers.splice(newIndex, 0, moved);
          // zIndex 재할당
          const updated = unitLayers.map((l, i) => ({ ...l, zIndex: i }));
          return {
            layers: {
              ...state.layers,
              [unitType]: updated,
            },
          };
        });
      },

      // printUnit 상태에서 레이어 목록 재동기화
      syncLayersForUnit: (unitType) => {
        set((state) => {
          const unit = state.printUnits[unitType];
          if (!unit) return {};
          const existing = state.layers[unitType] ?? [];
          return {
            layers: {
              ...state.layers,
              [unitType]: buildLayersFromUnit(unitType, unit, existing),
            },
          };
        });
      },

      // 새 앨범 만들기용 초기화 함수 (페이지 리로드 없음)
      resetForNewAlbum: () => {
        set({
          albumInfo: {
            title: '',
            artist: '',
            coverImage: null,
            tags: []
          },
          tracklist: [],
          printUnits: DEFAULT_PRINT_UNITS,
          selectedPrintUnit: PrintUnitType.FRONT,
          layers: buildInitialLayers(DEFAULT_PRINT_UNITS),
          selectedLayerId: null,
        });
      },

      // 전체 초기화 함수 — reload 없이 in-memory state를 동기적으로 리셋
      resetAll: () => {
        set({
          albumInfo: {
            title: '',
            artist: '',
            coverImage: null,
            tags: []
          },
          tracklist: [],
          printUnits: DEFAULT_PRINT_UNITS,
          selectedPrintUnit: PrintUnitType.FRONT,
          layers: buildInitialLayers(DEFAULT_PRINT_UNITS),
          selectedLayerId: null,
          activePrintUnitOptionTab: 'image',
        });
        // outpainting 다이얼로그/잡/프롬프트도 초기화
        useOutpaintingStore.getState().closeDialog();
        useOutpaintingStore.getState().setPrompt('');
        useOutpaintingStore.getState().setActiveJob(null);
        // localStorage persist 즉시 제거 — 다음 세션 복원 방지
        try {
          localStorage.removeItem('md-label-maker-storage');
        } catch (e) {
          void e;
        }
      },

      // 로컬 스토리지 리셋 함수
      resetStorage: () => {
        try {
          // 로컬 스토리지에서 상태 제거
          localStorage.removeItem('md-label-maker-storage');
          
          // 페이지 새로고침으로 전체 앱 상태를 초기화
          // 이렇게 하면 React 훅 규칙 위반을 방지할 수 있음
          window.location.reload();
        } catch (error) {
          console.error('초기화 중 오류 발생:', error);
        }
      }
    }),
    {
      name: 'md-label-maker-storage',
      version: 1,
      // QuotaExceededError 가드: persisted state에 base64 이미지가 누적되어
      // localStorage 5MB 한도를 초과하면 무거운 필드를 떨어내고 재시도한다.
      // 사용자에게는 "앨범 로드 실패" 다이얼로그 대신 graceful degradation.
      storage: createJSONStorage(() => ({
        getItem: (name) => localStorage.getItem(name),
        setItem: (name, value) => {
          try {
            localStorage.setItem(name, value);
          } catch (err: any) {
            if (err?.name !== 'QuotaExceededError') throw err;
            console.warn('[store] localStorage quota exceeded — stripping base64 images');
            const stripDataUrls = (obj: any) => {
              if (!obj || typeof obj !== 'object') return;
              for (const k of Object.keys(obj)) {
                const v = obj[k];
                if (typeof v === 'string' && v.startsWith('data:') && v.length > 1024) {
                  obj[k] = '';
                } else if (v && typeof v === 'object') {
                  stripDataUrls(v);
                }
              }
            };
            try {
              const parsed = JSON.parse(value);
              stripDataUrls(parsed);
              localStorage.setItem(name, JSON.stringify(parsed));
            } catch (retryErr) {
              console.error('[store] strip & retry failed; clearing storage', retryErr);
              localStorage.removeItem(name);
            }
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      })),
      migrate: (persistedState: any, version: number) => {
        // 기존 데이터가 있고 버전이 0이거나 없는 경우 (이전 버전)
        if (persistedState && persistedState.printUnits) {
          // 인쇄부 1 (FRONT)의 useText가 false인 경우 true로 변경
          if (persistedState.printUnits[PrintUnitType.FRONT]) {
            const frontUnit = persistedState.printUnits[PrintUnitType.FRONT];

            if (frontUnit.useText === false) {
              persistedState.printUnits[PrintUnitType.FRONT] = {
                ...frontUnit,
                useText: true,
                // 기본값이 없는 경우 추가
                showTitle: frontUnit.showTitle ?? true,
                showArtist: frontUnit.showArtist ?? true,
                text: frontUnit.text || { ...DEFAULT_TEXT_SETTINGS },
                titlePosition: frontUnit.titlePosition || { x: 60, y: 60 },
                artistPosition: frontUnit.artistPosition || { x: 60, y: 140 },
                titleFontSize: frontUnit.titleFontSize || 100,
                artistFontSize: frontUnit.artistFontSize || 70
              };
            }
          }

          // 인쇄부 4 (BACK)에 흰색 투명도 레이어 속성 추가
          if (persistedState.printUnits[PrintUnitType.BACK]) {
            const backUnit = persistedState.printUnits[PrintUnitType.BACK];

            // 새로운 속성들이 없는 경우 추가
            if (backUnit.showWhiteOverlay === undefined || backUnit.whiteOverlayOpacity === undefined) {
              persistedState.printUnits[PrintUnitType.BACK] = {
                ...backUnit,
                showWhiteOverlay: backUnit.showWhiteOverlay ?? false,
                whiteOverlayOpacity: backUnit.whiteOverlayOpacity ?? 0.5
              };
            }
          }

          // 모든 인쇄부의 showMdLogo를 적절히 설정
          Object.keys(persistedState.printUnits).forEach((key) => {
            const unit = persistedState.printUnits[key];
            if (unit) {
              // 인쇄부 2(SIDE), 3(BACK_TOP), 6(DISC_SIDE)는 로고 표시 안함
              const shouldShowLogo = ![PrintUnitType.SIDE, PrintUnitType.BACK_TOP, PrintUnitType.DISC_SIDE].includes(key as PrintUnitType);

              if (unit.showMdLogo !== shouldShowLogo) {
                persistedState.printUnits[key] = {
                  ...unit,
                  showMdLogo: shouldShowLogo
                };
              }

              // imageFilter 필드가 없는 경우 기본값 추가
              if (!unit.imageFilter) {
                persistedState.printUnits[key] = {
                  ...persistedState.printUnits[key],
                  imageFilter: { ...DEFAULT_IMAGE_FILTER }
                };
              }
            }
          });
        }
        
        return persistedState;
      },
      // 의도적으로 영속화할 필드만 명시 (whitelist 방식)
      // 함수/액션, clipboard(임시), undo/redo stack은 제외
      partialize: (state) => ({
        albumInfo: state.albumInfo,
        tracklist: state.tracklist,
        printUnits: state.printUnits,
        selectedPrintUnit: state.selectedPrintUnit,
        activePrintUnitOptionTab: state.activePrintUnitOptionTab,
        layers: state.layers,
      })
    }
  ),
  {
    limit: 50,
    // albumInfo와 tracklist 변경만 히스토리로 추적 (printUnits 이미지 데이터 포함 가능)
    // selectedPrintUnit 탭 전환, clipboard는 undo 대상에서 제외
    partialize: (state) => ({
      albumInfo: state.albumInfo,
      tracklist: state.tracklist,
      printUnits: state.printUnits,
    }),
  }
));

// 로컬 스토리지 문제 해결을 위한 수동 리셋 함수
export const resetLocalStorage = () => {
  localStorage.removeItem('md-label-maker-storage');
}